import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { isStoreBilledSubscription, SubscriptionProvider, SubscriptionStatus, Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { ITariffPlan, ITariffPlanPrice, ITariffPlanSubscription } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";
import { IStripeInvoice, IStripePaymentMethod, IStripePaymentError } from "@eagerdog/stripe";

import { apiService, webSocketService } from "src/services";
import { localStorageService } from "src/services/local-storage.service";
import { helperService } from "src/services/helper.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import AddUserPaymentMethod from "src/components/AddUserPaymentMethod";
import CardOnFile from "src/components/CardOnFile/CardOnFile";
import Button from "src/components/Button/Button";
import LoadingSpinner from "src/components/LoadingSpinner";
import { toast } from "src/components/Toast/ToastManager";
import { InvoicesGrid } from "./InvoicesGrid";

import styles from "./UserSubscriptionTab.module.scss";
import { SubscriptionChangeForm } from "./SubscriptionChangeForm";

const stripePromise = loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

interface UserSubscriptionTabProps {
  clubId?: string;
}

const UserSubscriptionTab: React.FC<UserSubscriptionTabProps> = ({ clubId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const user = helperService.getUser();
  // Dog Owner Premium is retired: owners get a notice instead of the upsell; club billing is unchanged.
  const ownerPremiumRetired = !clubId && !DOG_OWNER_PREMIUM_ENABLED && user.user_type === Constants.user_type.owner;

  const [subscription, setSubscription] = useState<ITariffPlanSubscription | null>(null);
  const [tariffPlan, setTariffPlan] = useState<ITariffPlan | null>(null);

  const [paymentMethods, setPaymentMethods] = useState<IStripePaymentMethod[]>([]);
  const [standaloneCustomerId, setStandaloneCustomerId] = useState<string | undefined>(user.stripe_customer_id);

  const [invoices, setInvoices] = useState<IStripeInvoice[]>([]);

  const [submittingSubscriptionChange, setSubmittingSubscriptionChange] = useState(false);
  const [waitingForWebhook, setWaitingForWebhook] = useState(false);

  const [latestPaymentError, setLatestPaymentError] = useState<IStripePaymentError | undefined>(undefined);
  
  // Track if initial load has completed (to distinguish "no subscription" from "still loading")
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Track WebSocket connection
  const isCheckoutReturnRef = useRef(false);
  const isLoadingRef = useRef(false);
  const lastUpdateTimeRef = useRef(0);

  const { isShown: showCancelConfirm, toggle: toggleCancelConfirm } = useModal();
  const { isShown: showSubscriptionModal, toggle: toggleSubscriptionModal } = useModal();
  const { isShown: showPaymentMethodsModal, toggle: togglePaymentMethodsModal } = useModal();

  const loadSubscriptionData = useCallback(async (isPolling = false, force = false) => {
    // Prevent concurrent loads (unless forced by WebSocket)
    if (isLoadingRef.current && !force) {
      return;
    }
    
    isLoadingRef.current = true;
    
    const refreshUserData = async () => {
      try {
        const user = await apiService.whoAmI();
        localStorageService.setItem(localStorageService.USER, JSON.stringify(user));
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    };

    const fetchSubscriptionData = async (subscription: ITariffPlanSubscription) => {
      try {
        const [tariffResponse, paymentMethodsResponse, invoices, paymentError] = await Promise.all([
          apiService.getTariffPlan(subscription.tariff_plan_id).catch(() => null),
          isStoreBilledSubscription(subscription)
            ? Promise.resolve([])
            : apiService.getUserPaymentMethods(subscription.provider_customer_external_id).catch(() => []),
          apiService.getSubscriptionInvoices(clubId).catch(() => []),
          apiService.getUserSubscriptionPaymentError().catch(() => undefined),
        ]);

        setTariffPlan(tariffResponse);
        setPaymentMethods(paymentMethodsResponse);
        setInvoices(invoices);
        setLatestPaymentError(paymentError);
        
        // Refresh user data to ensure localStorage has updated subscription status
        await refreshUserData();
        
        // Emit global event so other tabs/components know user data changed
        window.dispatchEvent(new CustomEvent('userDataUpdated'));
      } catch (err) {
        toast.show({
          title: "Subscription",
          content: "Could not load payment methods, please try again later",
          duration: 5000,
          type: "fail"
        });
      } finally {
        setLoading(false);
        setWaitingForWebhook(false);
        setInitialLoadComplete(true);
        isLoadingRef.current = false;
      }
    };

    if (!isPolling) {
      setLoading(true);
    }
    
    try {
      const response = await apiService.getUserSubscriptionPlan(clubId);
      if (response) {
        setSubscription(response);
        setWaitingForWebhook(false);
        await fetchSubscriptionData(response);
      } else {
        // No subscription — still ensure a Stripe customer exists and load any saved cards
        try {
          if (user.user_type === Constants.user_type.owner) {
            const { stripe_customer_id } = await apiService.ensureStripeCustomer();
            setStandaloneCustomerId(stripe_customer_id);
            const methods = await apiService.getSelfPaymentMethods();
            setPaymentMethods(methods);
          }
        } catch (_err) {
          // non-fatal
        }
        setLoading(false);
        setInitialLoadComplete(true);
        isLoadingRef.current = false;
      }
    } catch (error) {
      if (!isPolling) {
        toast.show({
          title: "Subscription",
          content: "Could not load subscription, please try again later",
          duration: 5000,
          type: "fail"
        });
      }
      setLoading(false);
      setInitialLoadComplete(true);
      isLoadingRef.current = false;
    }
  }, [clubId]);

  // Load subscription data once on mount
  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  // Check if returning from Stripe checkout
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isReturningFromCheckout = searchParams.has('session_id') || searchParams.has('success');
    
    if (isReturningFromCheckout) {
      isCheckoutReturnRef.current = true;
      setWaitingForWebhook(true);
      
      // Clean up URL parameters
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('session_id');
      newSearchParams.delete('success');
      const newSearch = newSearchParams.toString();
      navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // WebSocket connection and subscription updates
  useEffect(() => {
    const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
    if (!token) return;

    // Connect to subscription WebSocket
    webSocketService.connect({
      url: Config.web.REACT_APP_API_URL || 'http://localhost:3001',
      namespace: '/subscriptions',
      token,
    });

    // Listen for connection status
    const unsubscribeStatus = webSocketService.onConnectionStatus((status) => {
      // Connection status tracked internally by WebSocketService
    });

    // Listen for subscription updates
    const handleSubscriptionUpdate = (data: any) => {
      // Debounce multiple events (Stripe sends created + updated in quick succession)
      const now = Date.now();
      if (now - lastUpdateTimeRef.current < 3000) {
        return;
      }
      lastUpdateTimeRef.current = now;
      
      // Check if this is from checkout BEFORE resetting the flag
      const isFromCheckout = isCheckoutReturnRef.current;
      
      setWaitingForWebhook(false);
      isCheckoutReturnRef.current = false;
      
      // Reload subscription data silently - the action that triggered this already showed a toast
      // Force reload even if another load is in progress (WebSocket events are high priority)
      loadSubscriptionData(false, true);
      
      // Only show toast if returning from checkout (no other action triggered it)
      if (isFromCheckout) {
        toast.show({
          title: "Welcome to Premium!",
          content: "Your subscription is now active",
          duration: 5000,
          type: "success"
        });
      }
    };

    // Subscribe to subscription updates
    const unsubscribeEvent = webSocketService.onCustomEvent('subscription_updated', handleSubscriptionUpdate);

    // Cleanup on unmount
    return () => {
      unsubscribeStatus();
      unsubscribeEvent();
      webSocketService.disconnect();
    };
  }, [loadSubscriptionData]);

  // Fallback timeout if WebSocket doesn't arrive
  useEffect(() => {
    if (waitingForWebhook && isCheckoutReturnRef.current) {
      const timeout = setTimeout(() => {
        if (isCheckoutReturnRef.current) {
          setWaitingForWebhook(false);
          loadSubscriptionData();
        }
      }, 15000); // 15 second timeout

      return () => clearTimeout(timeout);
    }
  }, [waitingForWebhook, loadSubscriptionData]);

  // Payment error is now fetched as part of loadSubscriptionData
  // This effect is removed to prevent duplicate API calls

  const handleSubscriptionUpgrade = (price: ITariffPlanPrice) => {
    if (subscription) {
      setSubmittingSubscriptionChange(true);
      apiService.updateUserSubscriptionPlan({
        plan_type: "pro",
        price_provider_external_id: price.provider_external_id,
      })
        .then(() => {
          setSubscription({
            ...subscription,
            tariff_plan_type: "pro",
            current_price: price,
          });

          toast.show({
            title: "Subscription",
            content: "Subscription was successfully upgraded",
            duration: 5000,
            type: "success"
          });
        })
        .catch(() => {
          toast.show({
            title: "Subscription",
            content: "Could not upgrade the subscription, please try again later",
            duration: 5000,
            type: "fail"
          });
        })
        .finally(() => {
          toggleSubscriptionModal();
          setSubmittingSubscriptionChange(false);
        })
    }
  }

  const handleReactivateSubscription = () => {
    setLoading(true);
    apiService.reactivateUserSubscriptionPlan()
      .then(async () => {
        // Refresh subscription data to remove cancel_at
        try {
          const updatedSubscription = await apiService.getUserSubscriptionPlan(clubId);
          setSubscription(updatedSubscription);
          
          // Refresh user data to ensure localStorage is in sync
          const user = await apiService.whoAmI();
          localStorageService.setItem(localStorageService.USER, JSON.stringify(user));
          
          toast.show({
            title: "Subscription",
            content: "Subscription has been reactivated successfully!",
            duration: 5000,
            type: "success"
          });
        } catch (err) {
          console.error('Failed to refresh subscription data:', err);
          toast.show({
            title: "Subscription",
            content: "Subscription was reactivated",
            duration: 5000,
            type: "success"
          });
        }
      })
      .catch(() => {
        toast.show({
          title: "Subscription",
          content: "Could not reactivate the subscription, please try again later",
          duration: 5000,
          type: "fail"
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleCancelSubscription = () => {
    setLoading(true);
    apiService.cancelUserSubscriptionPlan()
      .then(async () => {
        // Backend has already updated the database, now fetch the updated subscription
        try {
          const updatedSubscription = await apiService.getUserSubscriptionPlan(clubId);
          
          if (!updatedSubscription) {
            // Subscription was immediately canceled (trial period) - clear state
            setSubscription(null);
            setTariffPlan(null);
            setPaymentMethods([]);
            
            // Refresh user data to ensure localStorage is in sync
            const user = await apiService.whoAmI();
            localStorageService.setItem(localStorageService.USER, JSON.stringify(user));
            
            toast.show({
              title: "Subscription",
              content: "Your subscription was canceled.",
              duration: 5000,
              type: "success"
            });
            return;
          }
          
          setSubscription(updatedSubscription);
          
          // Refresh user data to ensure localStorage is in sync
          const user = await apiService.whoAmI();
          localStorageService.setItem(localStorageService.USER, JSON.stringify(user));
          
          toast.show({
            title: "Subscription",
            content: "Subscription will be canceled at the end of your billing period. You'll keep access until then.",
            duration: 5000,
            type: "success"
          });
        } catch (err: any) {
          console.error('Failed to refresh subscription data:', err);
          console.error('Error details:', err.response?.data || err.message);
          
          // Likely immediate cancellation - clear state
          setSubscription(null);
          setTariffPlan(null);
          setPaymentMethods([]);
          
          // Refresh user data
          try {
            const user = await apiService.whoAmI();
            localStorageService.setItem(localStorageService.USER, JSON.stringify(user));
          } catch (e) {
            console.error('Failed to refresh user:', e);
          }
          
          toast.show({
            title: "Subscription",
            content: "Your subscription was canceled.",
            duration: 5000,
            type: "success"
          });
        }
      })
      .catch((err) => {
        console.error('Failed to cancel subscription:', err);
        toast.show({
          title: "Subscription",
          content: "Could not cancel the subscription, please try again later",
          duration: 5000,
          type: "fail"
        });
      })
      .finally(() => {
        toggleCancelConfirm();
        setLoading(false);
      })
  }

  const formatPrice = () => {
    const price = subscription?.current_price;

    if (price) {
      const formatter = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: price.currency,
        minimumFractionDigits: 2,
      });
      const period = price.billing_period;

      return [formatter.format(price.amount_cents / 100), "/", period].join(" ");
    }
  }

  const getDefaultPaymentMethod = () => {
    return paymentMethods.find(pm => pm.id === subscription?.provider_payment_method_external_id);
  }

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  }

  const storeBilled = isStoreBilledSubscription(subscription);
  const storeName =
    subscription?.provider === SubscriptionProvider.PLAY_STORE ? "Google Play"
    : subscription?.provider === SubscriptionProvider.TEST_STORE ? "the RevenueCat Test Store"
    : "the App Store";

  if (loading) {
    return (
      <div className={styles.UserSubscriptionTab}>
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className={styles.UserSubscriptionTab}>
      {waitingForWebhook && (
        <div className={styles.waitingState}>
          <LoadingSpinner />
          <div className={styles.waitingText}>
            <div className={styles.waitingTitle}>Processing your subscription...</div>
            <div className={styles.waitingSubtext}>This should only take a few moments</div>
          </div>
        </div>
      )}
      {!subscription && !tariffPlan && !waitingForWebhook && initialLoadComplete && ownerPremiumRetired && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🐾</div>
          <div className={styles.emptyStateContent}>
            <div className={styles.emptyStateTitle}>Premium is now included for everyone</div>
            <div className={styles.emptyStateText}>
              Ribbon Room, Stats, Run History and title tracking are free on every Eager Dog account.
              If you had a Dog Owner Premium subscription it has been cancelled and you will not be billed again.
            </div>
          </div>
        </div>
      )}
      {!subscription && !tariffPlan && !waitingForWebhook && initialLoadComplete && !ownerPremiumRetired && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🐾</div>
          <div className={styles.emptyStateContent}>
            <div className={styles.emptyStateTitle}>Unlock Your Full Potential with Premium!</div>
            <div className={styles.emptyStateText}>
              {user.user_type === Constants.user_type.club_manager
                ? "Unlimited pricing for courses and enhanced communication tools to communicate with your participants, volunteers and members."
                : "Unlock enhanced dog profiles, track your titles, view event results, and share your progress!"}
            </div>
          </div>
          <Button className={styles.emptyStateButton} onClick={() => navigate("/premium")}>Upgrade to Premium</Button>
        </div>
      )}

      {!subscription && initialLoadComplete && user.user_type === Constants.user_type.owner && standaloneCustomerId && (
        <div className={styles.subscriptionCard} style={{ marginTop: "24px" }}>
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.planInfo}>
                <span className={styles.planName}>Payment Method</span>
              </div>
            </div>
          </div>
          <div className={styles.paymentMethodBody}>
            {paymentMethods.length > 0 ? (
              <CardOnFile
                paymentMethod={paymentMethods[0]}
                customerId={standaloneCustomerId}
                onCardUpdated={(updated) => {
                  setPaymentMethods(updated);
                  toast.show({ title: "Payment Method", content: "Card updated successfully", duration: 4000, type: "success" });
                }}
              />
            ) : (
              <>
                <p className={styles.noCardText}>No card on file. Add one below to use for AKC waitlisted event entries.</p>
                <Elements stripe={stripePromise}>
                  <AddUserPaymentMethod
                    customerId={standaloneCustomerId}
                    hideTitle
                    onSave={async () => {
                      const updated = await apiService.getSelfPaymentMethods();
                      setPaymentMethods(updated);
                      toast.show({ title: "Payment Method", content: "Card saved successfully", duration: 4000, type: "success" });
                    }}
                  />
                </Elements>
              </>
            )}
          </div>
        </div>
      )}
      {!!subscription && <>
        {!!latestPaymentError && (
          <div className={styles.paymentError}>
            <span className={styles.errorIcon}>⚠️</span>
            <span>{latestPaymentError.message}</span>
          </div>
        )}

        <div className={styles.subscriptionCard}>
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.planInfo}>
                <span className={styles.planName}>Premium</span>
                <span className={`${styles.statusBadge} ${styles[subscription.status]}`}>
                  {subscription.status === SubscriptionStatus.TRIALING ? "Trial" : subscription.status}
                </span>
              </div>
              <div className={styles.priceInfo}>
                <span className={styles.price}>{formatPrice()}</span>
              </div>
            </div>
            {!storeBilled && getDefaultPaymentMethod()?.card && (
              <div className={styles.paymentMethodPreview}>
                <div className={styles.cardInfo}>
                  <span className={styles.cardBrand}>{formatCardBrand(getDefaultPaymentMethod()!.card!.brand)}</span>
                  <span className={styles.cardNumber}>•••• {getDefaultPaymentMethod()!.card!.last4}</span>
                </div>
                <Button
                  variant="secondary"
                  className={styles.changeButton}
                  onClick={() => togglePaymentMethodsModal()}
                >
                  Change
                </Button>
              </div>
            )}
          </div>
          
          <div className={styles.cardDetails}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Started</span>
              <span className={styles.detailValue}>{moment(subscription.start_date).format("MMM Do, YYYY")}</span>
            </div>
            {subscription.status === SubscriptionStatus.TRIALING && !!subscription.trial_ends_at && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Trial ends</span>
                <span className={styles.detailValue}>{moment(subscription.trial_ends_at).format("MMM Do, YYYY")}</span>
              </div>
            )}
            {!!subscription.next_invoice_at && !subscription.cancel_at && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Next payment</span>
                <span className={styles.detailValue}>{moment(subscription.next_invoice_at).format("MMM Do, YYYY")}</span>
              </div>
            )}
            {storeBilled ? (
              <div className={styles.detailItem}>
                {!!subscription.cancel_at && (
                  <span className={styles.detailValue}>
                    Subscription canceled - Access ends <strong>{moment(subscription.cancel_at).format("MMMM D, YYYY")}</strong>
                  </span>
                )}
                <span className={styles.detailLabel}>
                  Billed through {storeName}. To change or cancel, use the subscription settings on your phone.
                </span>
              </div>
            ) : subscription.cancel_at ? (
              <div className={styles.detailItem}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  backgroundColor: '#FFF6EB',
                  border: '1px solid #FFAD4A',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#333333'
                }}>
                  <span style={{ lineHeight: '1', whiteSpace: 'nowrap' }}>
                    Subscription canceled - Access ends <strong>{moment(subscription.cancel_at).format("MMMM D, YYYY")}</strong>
                  </span>
                  {!ownerPremiumRetired && (
                    <Button
                      variant="default"
                      onClick={handleReactivateSubscription}
                      style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      disabled={loading}
                    >
                      Resubscribe
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.detailItem}>
                <Button
                  variant="secondary"
                  className={styles.cancelButton}
                  onClick={() => toggleCancelConfirm()}
                >
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>

          {!storeBilled && !ownerPremiumRetired && subscription.tariff_plan_type !== "pro" && (
            <div className={styles.cardActions}>
              <Button
                className={styles.upgradeButton}
                onClick={() => toggleSubscriptionModal()}
              >
                Upgrade Subscription
              </Button>
            </div>
          )}
        </div>
      </>}

      {!!subscription && !storeBilled && <>
        <h4>Invoices</h4>
        <InvoicesGrid invoices={invoices}/>
      </>}

      <Modal
        className="userSubscriptionModal"
        isShown={showCancelConfirm}
        hide={() => {
          toggleCancelConfirm();
        }}
        modalContent={
          <Confirm
            message={
              <div style={{ lineHeight: '1.6' }}>
                <div style={{ marginBottom: '16px' }}>
                  Are you sure you want to cancel your subscription?
                </div>
                {subscription?.next_invoice_at && (
                  <div style={{ 
                    backgroundColor: '#FFF6EB',
                    border: '1px solid #FFAD4A',
                    borderRadius: '8px',
                    padding: '16px 20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#333333'
                  }}>
                    Your access to premium features will end on{' '}
                    <strong>{moment(subscription.next_invoice_at).format('MMMM D, YYYY')}</strong>
                  </div>
                )}
              </div>
            }
            submitting={loading}
            onConfirm={handleCancelSubscription}
            onCancel={() => {
              toggleCancelConfirm();
            }}
          />
        }
      />

      <Modal
        className="userSubscriptionModal"
        isShown={showSubscriptionModal}
        hide={() => {
          toggleSubscriptionModal();
        }}
        modalContent={
          <SubscriptionChangeForm
            submitting={submittingSubscriptionChange}
            onSubmit={(price) => {
              handleSubscriptionUpgrade(price);
            }}
          />
        }
      />

      <Modal
        className="paymentMethodsModal"
        isShown={showPaymentMethodsModal}
        hide={() => {
          togglePaymentMethodsModal();
        }}
        modalContent={
          <div className={styles.paymentMethodsModalContent}>
            <h3>Update Payment Method</h3>
            
            {getDefaultPaymentMethod()?.card && (
              <div className={styles.currentPaymentMethod}>
                <div className={styles.currentPaymentLabel}>Current Card</div>
                <div className={styles.currentPaymentCard}>
                  <span className={styles.cardBrandLarge}>{formatCardBrand(getDefaultPaymentMethod()!.card!.brand)}</span>
                  <span className={styles.cardDetails}>
                    •••• •••• •••• {getDefaultPaymentMethod()!.card!.last4}
                  </span>
                  <span className={styles.cardExpiry}>
                    Expires {String(getDefaultPaymentMethod()!.card!.exp_month).padStart(2, '0')}/{String(getDefaultPaymentMethod()!.card!.exp_year).slice(-2)}
                  </span>
                </div>
              </div>
            )}

            {!!(subscription?.provider_customer_external_id || standaloneCustomerId) && (
              <div className={styles.updatePaymentSection}>
                <div className={styles.updatePaymentLabel}>Enter New Card</div>
                <Elements stripe={stripePromise}>
                  <AddUserPaymentMethod
                    customerId={(subscription?.provider_customer_external_id || standaloneCustomerId)!}
                    hideTitle
                    onSave={async () => {
                      try {
                        const effectiveCustomerId = subscription?.provider_customer_external_id || standaloneCustomerId!;
                        // Fetch updated payment methods
                        const updatedMethods = await apiService.getUserPaymentMethods(effectiveCustomerId);
                        
                        // Find the newest payment method (most recently added)
                        const newPaymentMethod = updatedMethods.find(
                          pm => pm.id !== subscription?.provider_payment_method_external_id
                        );
                        
                        if (newPaymentMethod && subscription) {
                          // Set the new payment method as default for the subscription
                          await apiService.updateUserSubscriptionPlan({
                            default_payment_method: newPaymentMethod.id
                          });
                          
                          // Update local state
                          setSubscription({
                            ...subscription,
                            provider_payment_method_external_id: newPaymentMethod.id,
                          } as ITariffPlanSubscription);
                          setPaymentMethods([newPaymentMethod]);
                          
                          toast.show({
                            title: "Payment Method",
                            content: "Payment method updated successfully",
                            duration: 5000,
                            type: "success"
                          });
                          
                          togglePaymentMethodsModal();
                        } else {
                          setPaymentMethods(updatedMethods);
                        }
                      } catch (error) {
                        toast.show({
                          title: "Payment Method",
                          content: "Could not update payment method, please try again later",
                          duration: 5000,
                          type: "fail"
                        });
                      }
                    }}
                  />
                </Elements>
              </div>
            )}
          </div>
        }
      />

    </div>
  )
}

export { UserSubscriptionTab }
